import { useMemo } from "react";
import type { InfoLinksConfig } from "@/types/agent/info-links";
import type { RailItem } from "@/types/voice/resources-rail";
import { isSafeUrl } from "@/pages/agent-interfaces/live-agent/components/infoLinkIcons";

const formatSize = (bytes?: number): string | null => {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** "installation-manual.pdf" -> "PDF". Falls back to null for extensionless files. */
const extensionLabel = (fileName?: string): string | null => {
  const ext = fileName?.split(".").pop();
  if (!ext || ext.length > 5 || ext === fileName) return null;
  return ext.toUpperCase();
};

/**
 * Flattens the merchant's `info_links` config into the rail's chip list.
 *
 * Documents come first (they carry the download verb and are what visitors
 * most often leave for), then action links. Anything with an unsafe URL is
 * dropped here rather than at render time, so every consumer of the list can
 * treat `url` as safe.
 */
export const useRailItems = (resources?: InfoLinksConfig | null): RailItem[] =>
  useMemo(() => {
    const documents = (resources?.documents ?? [])
      .filter((doc) => doc.fileUrl && isSafeUrl(doc.fileUrl))
      .map<RailItem>((doc) => {
        const size = formatSize(doc.sizeBytes);
        const ext = extensionLabel(doc.fileName);
        return {
          id: doc.id,
          kind: "document",
          label: doc.label || doc.fileName,
          url: doc.fileUrl,
          fileName: doc.fileName || undefined,
          meta: [ext, size].filter(Boolean).join(" · ") || null,
        };
      });

    const links = (resources?.links ?? [])
      .filter((link) => isSafeUrl(link.url))
      .map<RailItem>((link) => ({
        id: link.id,
        kind: "link",
        label: link.label,
        url: link.url,
        icon: link.icon,
        meta: null,
      }));

    return [...documents, ...links];
  }, [resources]);

export default useRailItems;
